import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { RequireAuth } from '@/components/RequireAuth'
import { useAuthStore } from '@/lib/auth-store'
import { HomePage } from '@/pages/HomePage'
import { DownloadPage } from '@/pages/DownloadPage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { JoinPage } from '@/pages/JoinPage'
import { JoinArtistPage } from '@/pages/JoinArtistPage'
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { PipelinePage } from '@/pages/PipelinePage'
import { TerritoryPage } from '@/pages/TerritoryPage'
import { ShowsPage } from '@/pages/ShowsPage'
import { EpkSettingsPage } from '@/pages/EpkSettingsPage'
import { EpkDraftEditorPage } from '@/pages/EpkDraftEditorPage'
import { FinanceDocumentsPage } from '@/pages/FinanceDocumentsPage'
import { TourMailboxPage } from '@/pages/TourMailboxPage'
import { AudiencePage } from '@/pages/AudiencePage'
import { AudienceMapPage } from '@/pages/AudienceMapPage'
import { TicketingPage } from '@/pages/TicketingPage'
import { MerchPage } from '@/pages/MerchPage'
import { WellnessPage } from '@/pages/WellnessPage'
import { AssistantPage } from '@/pages/AssistantPage'
import { CrewDashboardPage } from '@/pages/CrewDashboardPage'
import { CrewProfilePage } from '@/pages/CrewProfilePage'
import { CrewAvailabilityPage } from '@/pages/CrewAvailabilityPage'
import { CrewEngagementsPage } from '@/pages/CrewEngagementsPage'
import { TalentPoolPage } from '@/pages/TalentPoolPage'
import { MarketingPage } from '@/pages/MarketingPage'
import { RiderPage } from '@/pages/RiderPage'
import { ShowDetailPage } from '@/pages/ShowDetailPage'
import { TeamPage } from '@/pages/TeamPage'
import { OrgSettingsPage } from '@/pages/OrgSettingsPage'
import { ArtistSettingsPage } from '@/pages/ArtistSettingsPage'
import { PerformancesPage } from '@/pages/PerformancesPage'
import { InboxPage } from '@/pages/InboxPage'
import { EmailRulesPage } from '@/pages/EmailRulesPage'
import { RehearsalsPage } from '@/pages/RehearsalsPage'
import { AdvancingPage } from '@/pages/AdvancingPage'
import { RosterPage } from '@/pages/RosterPage'
import { RosterPipelinePage } from '@/pages/RosterPipelinePage'
import { RosterUsersPage } from '@/pages/RosterUsersPage'
import { RosterBillingPage } from '@/pages/RosterBillingPage'
import { TravelPage } from '@/pages/TravelPage'
import { ToursPage } from '@/pages/ToursPage'
import { ContractsPage } from '@/pages/ContractsPage'
import { TourTemplatesPage } from '@/pages/TourTemplatesPage'
import { AnalyticsPage } from '@/pages/AnalyticsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { PlaceholderPage } from '@/pages/PlaceholderPage'
import { CancellationPage } from '@/pages/CancellationPage'
import { PressPage } from '@/pages/PressPage'
import { SetlistPage } from '@/pages/SetlistPage'
import { DebriefPage } from '@/pages/DebriefPage'
import { RoyaltiesPage } from '@/pages/RoyaltiesPage'
import { VipPage } from '@/pages/VipPage'
import { LogisticsPage } from '@/pages/LogisticsPage'
import { VenuesPage } from '@/pages/VenuesPage'
import { AdminPage } from '@/pages/AdminPage'
import { RateCrewPage } from '@/pages/RateCrewPage'
import { TestimonialFormPage } from '@/pages/TestimonialFormPage'
import { EpkPublicPage } from '@/pages/EpkPublicPage'
import { CrewChecklistPage } from './pages/CrewChecklistPage'
import { CrewPhotosPage } from '@/pages/CrewPhotosPage'
import { CrewItinerariesPage } from '@/pages/CrewItinerariesPage'
import { CrewMessagesPage } from '@/pages/CrewMessagesPage'
import { CrewChatPage } from '@/pages/CrewChatPage'
import { BrokerLoginPage } from '@/pages/BrokerLoginPage'
import { BrokerPortalPage } from '@/pages/BrokerPortalPage'
import { PaymentsPage } from '@/pages/PaymentsPage'
import { TeamPermissionsPage } from './pages/TeamPermissionsPage'
import { IntegrationsPage } from '@/pages/IntegrationsPage'

/** /join optionally takes ?invite=token — route to the accept page when present. */
function JoinRouter() {
  const [params] = useSearchParams()
  if (params.get('invite')) return <AcceptInvitationPage />
  return <JoinPage />
}

/**
 * Public homepage gate — authenticated users are sent straight to /dashboard
 * so the homepage is only ever visible to logged-out visitors.
 */
function HomepageGate() {
  const accessToken = useAuthStore((s) => s.accessToken)
  if (accessToken) return <Navigate to="/dashboard" replace />
  return <HomePage />
}

export default function App() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Public registration */}
      <Route path="/join" element={<JoinRouter />} />
      <Route path="/join/artist" element={<JoinArtistPage />} />

      {/* Public homepage — redirects to /dashboard when authenticated */}
      <Route path="/" element={<HomepageGate />} />
      <Route path="/download" element={<DownloadPage />} />

      {/* Public testimonial form (no auth) */}
      <Route path="/testimonial/:token" element={<TestimonialFormPage />} />
      <Route path="/epk/:slug/:token" element={<EpkPublicPage />} />

      {/* Broker portal (separate auth flow) */}
      <Route path="/broker/login" element={<BrokerLoginPage />} />
      <Route path="/broker-portal" element={<BrokerPortalPage />} />

      {/* Authenticated app */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/territory" element={<TerritoryPage />} />
        <Route path="/shows" element={<ShowsPage />} />
        <Route path="/shows/:id" element={<ShowDetailPage />} />
        <Route path="/ticketing" element={<TicketingPage />} />
        <Route path="/merch" element={<MerchPage />} />
        <Route path="/brokers" element={<Navigate to="/marketing?tab=brokers" replace />} />
        <Route path="/wellness" element={<WellnessPage />} />
        <Route path="/marketing" element={<MarketingPage />} />
        <Route path="/rider" element={<RiderPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/org/settings" element={<OrgSettingsPage />} />
        <Route path="/artist/settings" element={<Navigate to="/settings/artist" replace />} />
        <Route path="/settings/artist" element={<ArtistSettingsPage />} />
        <Route path="/performances" element={<PerformancesPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/email-rules" element={<EmailRulesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/epks" element={<EpkSettingsPage />} />
        <Route path="/settings/epks/drafts/:id" element={<EpkDraftEditorPage />} />
        <Route path="/finance/documents" element={<FinanceDocumentsPage />} />
        <Route path="/audience" element={<AudiencePage />} />
        <Route path="/audience/map" element={<AudienceMapPage />} />
        <Route path="/tours" element={<ToursPage />} />
        <Route path="/tours/:tourId/mail" element={<TourMailboxPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/tour-templates" element={<TourTemplatesPage />} />
        <Route path="/travel" element={<TravelPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/assistant" element={<AssistantPage />} />

        {/* Crew (self) */}
        <Route path="/crew/dashboard" element={<CrewDashboardPage />} />
        <Route path="/crew/profile" element={<CrewProfilePage />} />
        <Route path="/crew/availability" element={<CrewAvailabilityPage />} />
        <Route path="/crew/engagements" element={<CrewEngagementsPage />} />
        <Route path="/crew/checklist" element={<CrewChecklistPage />} />
        <Route path="/crew/photos" element={<CrewPhotosPage />} />
        <Route path="/crew/itineraries" element={<CrewItinerariesPage />} />
        <Route path="/crew/messages" element={<CrewMessagesPage />} />
        <Route path="/crew/messages/:channelId" element={<CrewChatPage />} />

        {/* Crew (artist-facing) */}
        <Route path="/crew/pool" element={<TalentPoolPage />} />
        <Route path="/rate-crew" element={<RateCrewPage />} />

        {/* Rehearsals + Advancing */}
        <Route path="/rehearsals" element={<RehearsalsPage />} />
        <Route path="/advancing" element={<AdvancingPage />} />

        {/* Additional modules */}
        <Route path="/cancellations" element={<CancellationPage />} />
        <Route path="/press" element={<PressPage />} />
        <Route path="/setlists" element={<SetlistPage />} />
        <Route path="/debriefs" element={<DebriefPage />} />
        <Route path="/royalties" element={<RoyaltiesPage />} />
        <Route path="/vip" element={<VipPage />} />
        <Route path="/venues" element={<VenuesPage />} />
        <Route path="/logistics" element={<LogisticsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/team-permissions" element={<TeamPermissionsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />

        {/* Stripe Connect callbacks (redirect from API) */}
        <Route path="/stripe/connect/return" element={<Navigate to="/crew/profile?stripe=connected" replace />} />
        <Route path="/stripe/connect/refresh" element={<Navigate to="/crew/profile?stripe=refresh" replace />} />

        {/* Roster (org) */}
        <Route path="/roster" element={<RosterPage />} />
        <Route path="/roster/pipeline" element={<RosterPipelinePage />} />
        <Route path="/roster/users" element={<RosterUsersPage />} />
        <Route path="/roster/billing" element={<RosterBillingPage />} />

        {/* Platform admin */}
        <Route path="/admin" element={<AdminPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
